/** Unicode letters and spaces only (no digits or punctuation). */
const INVALID_NAME_CHARS = /[^\p{L}\s]/gu;
const PERSON_NAME_VALUE = /^[\p{L}]+(?:\s+[\p{L}]+)*$/u;

export const PERSON_NAME_MIN = 2;
export const PERSON_NAME_MAX = 50;

const MAX_PHONE_DIGITS = 15;
const MIN_PHONE_DIGITS = 8;

export function sanitizePersonName(raw: string): string {
  return raw.replace(INVALID_NAME_CHARS, '').replace(/\s+/g, ' ');
}

export function validatePersonName(
  value: string,
  label: string,
  opts?: { required?: boolean; min?: number; max?: number },
): string | undefined {
  const trimmed = value.trim();
  const min = opts?.min ?? PERSON_NAME_MIN;
  const max = opts?.max ?? PERSON_NAME_MAX;
  if (!trimmed) {
    return opts?.required ? `${label} is required` : undefined;
  }
  if (trimmed.length < min) {
    return `${label} must be at least ${min} characters`;
  }
  if (trimmed.length > max) {
    return `${label} must be ${max} characters or less`;
  }
  if (/[^\p{L}\s'-]/u.test(trimmed) || !PERSON_NAME_VALUE.test(trimmed)) {
    return `${label} can only contain letters (no numbers or special characters)`;
  }
  return undefined;
}

export function sanitizePhoneNumber(raw: string): string {
  const hasPlus = raw.trimStart().startsWith('+');
  const digits = raw.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS);
  return hasPlus ? `+${digits}` : digits;
}

/** Customer lookup — letters, digits, spaces, @ . - + only (no other special characters). */
export function sanitizeCustomerSearch(raw: string): string {
  return raw.replace(/[^\p{L}\p{N}\s@.\-+]/gu, '');
}

export const SCHEDULE_MIN_LEAD_MINUTES = 30;

export function minScheduleDateTimeLocal(minutesFromNow = SCHEDULE_MIN_LEAD_MINUTES): string {
  const d = new Date(Date.now() + minutesFromNow * 60_000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function validateScheduledRideTime(
  value: string,
  opts?: { required?: boolean; minLeadMinutes?: number },
): string | undefined {
  const required = opts?.required ?? true;
  const minLead = opts?.minLeadMinutes ?? SCHEDULE_MIN_LEAD_MINUTES;
  const trimmed = value.trim();
  if (!trimmed) {
    return required ? 'Scheduled time is required' : undefined;
  }
  const at = new Date(trimmed);
  if (Number.isNaN(at.getTime())) {
    return 'Enter a valid scheduled date and time';
  }
  const diffMin = (at.getTime() - Date.now()) / 60_000;
  if (diffMin < minLead) {
    return `Scheduled trips must be at least ${minLead} minutes from now`;
  }
  return undefined;
}

export function parsePhoneLength(raw?: string | number | null): { min: number; max: number } | null {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  const range = s.match(/^(\d+)\s*(?:[-–to,]+)\s*(\d+)$/i);
  if (range) {
    const min = Number(range[1]);
    const max = Number(range[2]);
    if (min > 0 && max >= min && max <= 15) return { min, max };
  }
  const n = Number.parseInt(s, 10);
  if (Number.isFinite(n) && n > 0 && n <= 15) return { min: n, max: n };
  return null;
}

export function validateLocalPhoneForCountry(
  localNumber: string,
  length: { min: number; max: number } | null,
  label = 'Phone number',
  opts?: { required?: boolean; dialCode?: string },
): string | undefined {
  const digits = localNumber.replace(/\D/g, '');
  if (!digits) {
    return opts?.required ? `${label} is required` : undefined;
  }
  if (!/^\d+$/.test(digits)) {
    return `${label} can only contain numbers`;
  }
  const bounds = length || { min: MIN_PHONE_DIGITS, max: MAX_PHONE_DIGITS };
  const dial = opts?.dialCode ? ` (${opts.dialCode})` : '';
  if (digits.length < bounds.min || digits.length > bounds.max) {
    return bounds.min === bounds.max
      ? `${label} must be exactly ${bounds.min} digits for country code${dial} (entered ${digits.length})`
      : `${label} must be ${bounds.min}–${bounds.max} digits for country code${dial} (entered ${digits.length})`;
  }
  return undefined;
}

export function validateEmail(
  value: string,
  label = 'Email',
  opts?: { required?: boolean },
): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return opts?.required ? `${label} is required` : undefined;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return `Enter a valid ${label.toLowerCase()} address (e.g. name@example.com)`;
  }
  return undefined;
}

export function validatePhoneNumber(
  value: string,
  label = 'Phone number',
  opts?: { required?: boolean },
): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return opts?.required ? `${label} is required` : undefined;
  }
  if (/[^\d+]/.test(trimmed) || (trimmed.includes('+') && !trimmed.startsWith('+'))) {
    return `${label} can only contain numbers`;
  }
  const digits = trimmed.replace(/\D/g, '');
  if (!digits || /[A-Za-z]/.test(trimmed)) {
    return `${label} can only contain numbers`;
  }
  if (digits.length < MIN_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS) {
    return `${label} must be ${MIN_PHONE_DIGITS}–${MAX_PHONE_DIGITS} digits`;
  }
  if (!/^\+?\d+$/.test(trimmed)) {
    return `${label} can only contain numbers`;
  }
  return undefined;
}
